import { Component, ContentChild, Input, OnInit, ViewChild } from '@angular/core';
import { MatTabGroup, MatTabsModule } from '@angular/material/tabs';
import { MbBaseComponent, MbLoadingComponent, __ } from '@montblancsimpl/core';
import { BehaviorSubject, interval, NEVER, Subscription } from 'rxjs';
import { delay, switchMap } from 'rxjs/operators';
import { MbCarouselItemDirective } from './carousel-item.directive';
import { MbImageComponent } from '@montblancsimpl/files';
import { CommonModule } from '@angular/common';

/**
 * Displays the provided entities of type T inside a mat tab group.
 * The user can implement a custom template for the tabs or specify,
 * whether the tabs should change automatically.
 *
 * To use this component you need to specify entities of type (T | string)[].
 *
 * Minimum eample:
```
<mb-carousel [entities]="myArray"></mb-carousel>
```
 * You can specify, whether the tabs in the carousel should automatically change:
```
<mb-carousel [entities]="myArray" [autoplay]="true"></mb-carousel>
```
 * Per default, tabs will change every 5000 milliseconds, if autoplay is enabled.
 * You can specify the autoplay speed with the following input:
```
<mb-carousel [entities]="myArray" [autoplay]="true" [autoplaySpeed]="10000"></mb-carousel>
```
 *
 * Implement your own tab contents using the *mbCarouselItem structural directive.
 * It exposes each entity and the individual index. You can bind to the variables
 * using the following code.
 * In the example below, myArray is: [{ text: 'First text' }, { text: 'Seconds text' }],
 * therefore 'entity' will be a single object from the array.
 * You can access the 'text' property of each entity.
```
<mb-carousel [entities]="myArray">
    <ng-container *mbCarouselItem="let entity; let i = index">
      {{ entity.text }}
    </ng-container>
</mb-carousel>
```
 */
@Component({
    selector: 'mb-carousel',
    templateUrl: './carousel.component.html',
    styleUrls: ['./carousel.component.scss'],
    imports: [CommonModule, MatTabsModule]
})
export class MbCarouselComponent<T> extends MbBaseComponent implements OnInit {
  /**
   * Sets the starting tab of the mat tab group. Has to be a valid
   * number ranging from 0 to n-1 with n being the entities length
   */
  @Input() selectedIndex: number = 0;

  /**
   * The entities which should be displayed inside a mat tab group.
   * Each entity will be bound to one tap and template ref.
   */
  @Input() entities: (T | string)[] = [];

  private _autoplay: boolean = false;

  /**
   * If enabled, the tabs will automatically change in an ascending order.
   * Per default (false), the tabs won't change automatically.
   */
  @Input()
  get autoplay(): boolean {
    return this._autoplay;
  }
  set autoplay(value: boolean) {
    this._autoplay = value;
    if (value === true) {
      this.startAutoplay();
    } else if (value === false) {
      this.stopAutoplay();
    }
  }

  /**
   * Defines the speed of the automatic tab change in milliseconds.
   * The default ist 5000 milliseconds.
   */
  @Input() autoplaySpeed: number = 5000;

  /**
   * If enabled, the navigation items for the tabs will be displayed
   * below the carousel
   */
  @Input() showNavigationItems: boolean = true;

  /**
   * If enabled, the template for the previous action will be shown.
   * The previous template shows the previous tab on click.
   */
  @Input() showPrevious: boolean = true;

  /**
   * If enabled, the template for the next action will be shown.
   * The next template shows the next tab on click.
   */
  @Input() showNext: boolean = true;

  /**
   * The carousel item directive which holds a reference to the user specified template.
   * If the user has not specified a template, a default template will be used, which
   * enumerates the provided entities.
   */
  @ContentChild(MbCarouselItemDirective) row: MbCarouselItemDirective<T>;

  /**
   * Holds a reference to the mat tab group to control the tabs.
   */
  @ViewChild(MatTabGroup) tabGroup: MatTabGroup;

  /**
   * If this behavior subject emits true, the autoplay timer will be paused, until
   * it is being resetted or started again. If false is emitted, the autoplay will resume,
   * if it previously has been paused
   */
  private pauser$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  /**
   * A reference to the timer (interval) observable to be able to unsubscribe it.
   */
  private intervalSubscription: Subscription;

  constructor() {
    super();
  }

  ngOnInit(): void {
    if (this.autoplay === true) {
      this.startAutoplay();
    }
  }

  /**
   * Displays the tab with the index next to the selected one. If the index is out of bounds,
   * the first tab is selected. If autoplay is enabled and true is passed as the 'restartAutoplay'
   * parameter, the autoplay will be restarted
   *
   * @param [restartAutoplay=true] If true, the autoplay will be restarted
   */
  next(restartAutoplay: boolean = true): void {
    const tabsCount = this.tabGroup._tabs.length;

    if (this.tabGroup.selectedIndex + 1 === tabsCount) {
      this.selectedIndex = 0;
    } else {
      this.selectedIndex = this.tabGroup.selectedIndex + 1;
    }

    if (this.autoplay === true && restartAutoplay === true) {
      this.restartAutoplay();
    }
  }

  /**
   * Displays the tab with the index previous to the selected one. If the index is out of bounds,
   * the last tab is selected.
   *
   * @param [restartAutoplay=true] If true, the autoplay will be restarted
   */
  previous(restartAutoplay: boolean = true): void {
    const tabsCount = this.tabGroup._tabs.length;

    if (this.selectedIndex - 1 < 0) {
      this.selectedIndex = tabsCount - 1;
    } else {
      this.selectedIndex = this.tabGroup.selectedIndex - 1;
    }

    if (this.autoplay === true && restartAutoplay === true) {
      this.restartAutoplay();
    }
  }

  /**
   * Displays the tab with the provided tab index
   *
   * @param index The index of the tab that should be displayed
   * @param [restartAutoplay=true] If true, the autoplay will be restarted
   */
  show(index: number, restartAutoplay: boolean = true): void {
    if (
      __.IsNullOrUndefined(index) ||
      isNaN(index) ||
      index < 0 ||
      index >= this.tabGroup._tabs.length
    ) {
      return;
    }

    this.selectedIndex = index;

    if (this.autoplay === true && restartAutoplay === true) {
      this.restartAutoplay();
    }
  }

  /**
   * Starts the autoplay feature using the provided 'autoplaySpeed' to show the next tab
   * after n milliseconds. If the timer has already started it is being resetted.
   *
   * @param [_delay=0] You can optionally delay the new autoplay by passing in a number
   * indicating the delay in milliseconds. The default is 0 milliseconds.
   */
  startAutoplay(_delay: number = 0): void {
    const timer = interval(this.autoplaySpeed);

    if (!__.IsNullOrUndefined(this.intervalSubscription)) {
      this.intervalSubscription.unsubscribe();
      this.intervalSubscription = null;
    }

    this.intervalSubscription = this.pauser$
      .pipe(
        switchMap((value: boolean) => {
          return value === true ? NEVER : timer.pipe(delay(_delay));
        }),
      )
      .subscribe({
        next: (_) => this.next(false),
      });

    super.addSubscription(this.intervalSubscription);
  }

  /**
   * Stops the autoplay of the carousel until it is
   * being started again with "startAutoplay()".
   */
  stopAutoplay(): void {
    if (!__.IsNullOrUndefined(this.intervalSubscription)) {
      this.intervalSubscription.unsubscribe();
      this.intervalSubscription = null;
    }
  }

  /**
   * If the autoplay is currently runnig it will be paused.
   */
  pauseAutoplay(): void {
    this.pauser$.next(true);
  }

  /**
   * If the autoplay is currently pausing it will be resumed.
   */
  resumeAutoplay(): void {
    this.pauser$.next(false);
  }

  /**
   * Resets the autoplay timer. E.g., if 3000 milliseconds have passed for an autoplay
   * with a speed of 5000 milliseconds, the autoplay will start anew waiting for
   * 5000 milliseconds to show the next tab.
   *
   * @param [_delay=0] You can optionally delay the new autoplay by passing in a number
   * indicating the delay in milliseconds. The default is 0 milliseconds.
   */
  restartAutoplay(_delay: number = 0): void {
    this.startAutoplay();
  }
}
